import { useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Release } from '@/services/configurator/Release';
import { PlannerEnv } from '@/utils/env';
import { AlertTriangle, SendIcon, UploadCloud } from 'lucide-react';
import { useUser } from '@/components/providers/user-provider';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

export type PlannerPublishDialogProps = {
  name: string;
  id: string;
  onPublish: () => void;
  publishToEnv: PlannerEnv;
};

export function PlannerPublishDialog(props: PlannerPublishDialogProps) {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [forceUpdatePhraseKeys, setForceUpdatePhraseKeys] = useState(false);
  const { role } = useUser();
  const isAdmin = role === 'admin';

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        setOpen(open);
        if (!open) {
          setForceUpdatePhraseKeys(false);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button
          className='gap-2 h-8 border-solid border-2 rounded p-4 border-black'
          disabled={isLoading}
          isLoading={isLoading}
        >
          <SendIcon className='h-4 w-4 min-[800px]:hidden' />
          <span className='max-[800px]:hidden'>Publish</span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{`Publish ${props.name}?`}</DialogTitle>
          <DialogDescription>
            {`Publish staged data? ID: ${props.id}`}
          </DialogDescription>
        </DialogHeader>

        {isAdmin && (
          <div className='py-4'>
            <div className='flex items-center space-x-2'>
              <Checkbox
                id='force-update-phrase-keys'
                checked={forceUpdatePhraseKeys}
                onCheckedChange={(checked) =>
                  setForceUpdatePhraseKeys(checked === true)
                }
              />
              <label
                htmlFor='force-update-phrase-keys'
                className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
              >
                Force update existing Phrase keys
              </label>
            </div>
            {forceUpdatePhraseKeys && (
              <Alert className='mt-4 border-amber-500 bg-amber-50'>
                <AlertTriangle className='h-4 w-4 text-amber-500' />
                <AlertTitle className='text-amber-700'>
                  Warning: SEO Impact
                </AlertTitle>
                <AlertDescription className='text-amber-600'>
                  This will delete and recreate existing Phrase keys, which
                  destroys SEO metadata (meta keywords, descriptions) and other
                  translations that have been refined in Phrase. Only use this
                  as a last resort.
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter className='sm:justify-end'>
          <Button
            type='button'
            variant='secondary'
            onClick={() => {
              setOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            type='button'
            variant='default'
            onClick={() => {
              setIsLoading(true);
              setOpen(false);
              new Release(props.publishToEnv)
                .release(props.id, { forceUpdatePhraseKeys })
                .then(async () => {
                  toast.success(`Published ${props.name}`);

                  props.onPublish();
                })
                .catch((e) => {
                  const errorMessage = e.message
                    ? e.message
                    : 'An unknown error occurred';
                  toast.error(
                    `Failed to publish ${props.name} - ${errorMessage}`,
                  );
                })
                .finally(() => {
                  setIsLoading(false);
                });
            }}
          >
            <UploadCloud /> Publish
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
